import * as React from 'react';

import styled from '@emotion/styled';
import { useMediaQuery } from '@react-hook/media-query';
import { SkeletonPlaceholder } from 'src/shared/ds/components/SkeletonPlaceholder';
import { mediaQuery } from 'src/shared/ds/variables';
import { pxToRem } from 'src/shared/helpers/pxToRem';
import { BodyTextXSmall, ComponentTextSmall, HeadingText30, HeadingText40 } from '../Typography';

export interface SectionHeaderProps {
    title: string;
    secondaryText?: string;
    onSecondaryTextPress?: () => void;
    noMarginTop?: boolean;
}

export enum SectionHeaderTestID {
    SecondaryTextButton = 'SectionHeaderTestID.SecondaryTextButton',
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
    title,
    secondaryText,
    onSecondaryTextPress,
    noMarginTop = false,
}) => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));
    const shouldShowSecondaryTextButton = secondaryText && onSecondaryTextPress;

    const TextComponent = smallScreenDetected ? HeadingText30 : HeadingText40;
    const SecondaryTextComponent = smallScreenDetected ? BodyTextXSmall : ComponentTextSmall;

    return (
        <Container noMarginTop={noMarginTop}>
            <TextComponent>{title}</TextComponent>
            {shouldShowSecondaryTextButton && (
                <SecondaryTextButton
                    onClick={onSecondaryTextPress}
                    data-testid={SectionHeaderTestID.SecondaryTextButton}
                >
                    <SecondaryTextComponent fontColor="blue500" marginTop={3}>
                        {secondaryText}
                    </SecondaryTextComponent>
                </SecondaryTextButton>
            )}
        </Container>
    );
};

export interface SectionHeaderSkeletonProps {
    showSecondaryButton?: boolean;
    noMarginTop?: boolean;
}

export const SectionHeaderSkeleton: React.FC<SectionHeaderSkeletonProps> = ({
    showSecondaryButton,
    noMarginTop = false,
}) => (
    <Container noMarginTop={noMarginTop}>
        <SkeletonPlaceholder width={180} height={25} borderRadius={4} />
        {showSecondaryButton && <SkeletonPlaceholder width={50} height={25} borderRadius={4} />}
    </Container>
);

const Container = styled.div<{ noMarginTop: boolean }>(({ noMarginTop }) => ({
    marginBottom: pxToRem(20),
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    display: 'flex',
    marginTop: noMarginTop ? 0 : pxToRem(48),
    [mediaQuery.maxWidth('xl')]: {
        marginTop: noMarginTop ? 0 : pxToRem(24),
    },
}));

const SecondaryTextButton = styled.div({
    height: '100%',
    cursor: 'pointer',
});
